import { useState } from "react";
import "./CodeInput.css";

export interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: boolean;
  "aria-label"?: string;
}

/**
 * One-time code boxes (email / SMS codes).
 * One real input sits on top of the boxes, so typing, paste, backspace and
 * the phone's "fill code from message" all work like a normal field.
 * Example: <CodeInput value={otp} onChange={setOtp} autoFocus />
 */
const CodeInput = ({
  value,
  onChange,
  length = 6,
  autoFocus,
  disabled,
  error,
  "aria-label": ariaLabel,
}: CodeInputProps) => {
  const [focused, setFocused] = useState(false);
  const code = (value ?? "").slice(0, length);
  // the box the next digit goes into (the last one once the code is full)
  const active = Math.min(code.length, length - 1);

  return (
    <div
      className={`ds-code${disabled ? " is-disabled" : ""}${
        error ? " has-error" : ""
      }`}
      style={{ "--ds-code-length": length } as React.CSSProperties}
    >
      {Array.from({ length }, (_, i) => (
        <span
          key={i}
          aria-hidden="true"
          className={`ds-code__box${code[i] ? " is-filled" : ""}${
            focused && i === active ? " is-active" : ""
          }`}
        >
          {code[i] ??
            (focused && i === active && <span className="ds-code__caret" />)}
        </span>
      ))}
      <input
        className="ds-code__input"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={length}
        value={code}
        autoFocus={autoFocus}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={error || undefined}
        onFocus={(e) => {
          setFocused(true);
          // keep the cursor at the end, like the boxes show
          const end = e.target.value.length;
          e.target.setSelectionRange(end, end);
        }}
        onBlur={() => setFocused(false)}
        onChange={(e) =>
          onChange(e.target.value.replace(/\D/g, "").slice(0, length))
        }
      />
    </div>
  );
};

export default CodeInput;
